import React from "react";
import Dropdown from "../../../../common/components/Forms/Dropdown/Dropdown";
import Checkbox from "../../../../common/components/Forms/Checkbox/Checkbox";
import Tooltip from "../../../../common/components/Tooltips/Tooltip";
import { useFormikContext } from "formik";
import { IClientSettingsBoxProduct } from "../ClientSettingsBox/ClientSettingsBox";
import cx from "classnames";
import ProductType from "../../../../common/interfaces/enums/ProductType";
import { IQuoteSettings } from "../../../interfaces/form/IQuoteSettings";
import useSettingsValidation from "../../../hooks/useSettingsValidation";

type ClientSettingsBoxHealthProps = IClientSettingsBoxProduct;

const ClientSettingsBoxHealth: React.FC<ClientSettingsBoxHealthProps> = (props) => {
    const { client, name, setClient } = props;

    const { handleChange, handleBlur } = useFormikContext<IQuoteSettings>();

    const { getFormikError } = useSettingsValidation();

    const excessOptions = [
        { value: "0", label: "Nil" },
        { value: "250", label: "$250" },
        { value: "500", label: "$500" },
        { value: "1000", label: "$1,000" },
        { value: "2000", label: "$2,000" },
        { value: "4000", label: "$4,000" },
        { value: "5000", label: "$5,000" },
        { value: "6000", label: "$6,000" },
        { value: "10000", label: "$10,000" }
    ].map((i) => ({
        ...i,
        selected: client[ProductType.Health]?.excess.toString() === i.value
    }));

    return (
        <React.Fragment>
            <Dropdown
                id={`${name}.${ProductType.Health}.excess`}
                name={`${name}.${ProductType.Health}.excess`}
                title={`Excess`}
                selectedOptionId={client[ProductType.Health]?.excess.toString()}
                options={excessOptions}
                onChange={(e) =>
                    setClient({
                        ...client,
                        [ProductType.Health]: {
                            ...client[ProductType.Health],
                            excess: parseInt(e.target.value)
                        }
                    })
                }
                onBlur={handleBlur}
                tooltip={
                    <Tooltip tooltipContent="To give an example of how an excess works: If the medical bill is $5,000 and your excess is $250, you pay $250 and the insurer pays $4,750." />
                }
                hasError={getFormikError(`${name}.${ProductType.Health}.excess`)}
            />

            <div className={cx("layout-flex-column", "flex-gap-s")}>
                <p className={cx("text-bold", "text-font-size-s", "gap-s")}>
                    Add extras to your cover
                </p>

                <Checkbox
                    id={`${name}.${ProductType.Health}.hasSpecialistsAndTestsOption`}
                    name={`${name}.${ProductType.Health}.hasSpecialistsAndTestsOption`}
                    label={"Specialists & Tests"}
                    checked={client[ProductType.Health]?.hasSpecialistsAndTestsOption}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="m"
                    className="gap-s"
                    tooltipContent={
                        "Health Cover pays for the costs of hospitalisation (hospital treatment, surgeon's fees, etc). It will also cover specialist visits and tests before and after hospitalisation."
                    }
                    hasError={getFormikError(
                        `${name}.${ProductType.Health}.hasSpecialistsAndTestsOption`
                    )}
                />

                <Checkbox
                    id={`${name}.${ProductType.Health}.hasGPOption`}
                    name={`${name}.${ProductType.Health}.hasGPOption`}
                    label={"GP Option"}
                    checked={client[ProductType.Health]?.hasGPOption}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="m"
                    className="gap-s"
                    tooltipContent={
                        "This add-on provides cover for some day-to-day medical expenses like GP visits and prescription costs. Strict cover maximums apply."
                    }
                    hasError={getFormikError(`${name}.${ProductType.Health}.hasGPOption`)}
                />

                <Checkbox
                    id={`${name}.${ProductType.Health}.hasDentalAndOpticalOption`}
                    name={`${name}.${ProductType.Health}.hasDentalAndOpticalOption`}
                    label={"Dental & Optical Option"}
                    checked={client[ProductType.Health]?.hasDentalAndOpticalOption}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    size="m"
                    tooltipContent={
                        "This add-on covers some of the costs of dental and optical care. Strict cover maximums apply."
                    }
                    hasError={getFormikError(
                        `${name}.${ProductType.Health}.hasDentalAndOpticalOption`
                    )}
                />
            </div>
        </React.Fragment>
    );
};

export default ClientSettingsBoxHealth;
